import { forwardRef } from 'react';
import Select, { type GroupBase, type Props as StateManagerProps, type Theme } from 'react-select';
import Creatable from 'react-select/creatable';
import InputError from '../Error';
import Label from '../Label';

import './ReactSelect.css';

export interface ReactSelectProps<Option = unknown, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>
  extends Omit<StateManagerProps<Option, IsMulti, Group>, 'styles'> {
  error?: string;
  label?: string;
  isCreatable?: boolean;
  isValidNewOption?: (inputValue: string) => boolean;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  /** Compare option objects when values are not referentially equal (react-select v4 compat / custom objects). */
  isOptionEqualToValue?: (option: unknown, selectValue: unknown) => boolean;
}

const isNonEmptyNewOption = (inputValue: string) => Boolean(inputValue?.trim());

function ReactSelectInner<Option = unknown, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  { error, label, formatCreateLabel, isValidNewOption = isNonEmptyNewOption, ...rest }: ReactSelectProps<Option, IsMulti, Group>,
  ref: React.Ref<any>
) {
  const { id, isCreatable } = rest;
  const labelView = label && <Label htmlFor={id}>{label}</Label>;
  const errorView = error && <InputError>{error}</InputError>;
  const customizations = {
    classNamePrefix: 'react-select',
    className: 'custom-container',
    menuPortalTarget: document.body,
    styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
    theme: (theme: Theme) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#10B981',
        primary25: '#F3F4F6',
      },
    }),
  };
  if (isCreatable) {
    return (
      <>
        {labelView}
        <Creatable {...rest} ref={ref} {...customizations} isValidNewOption={isValidNewOption} formatCreateLabel={formatCreateLabel} />
        {errorView}
      </>
    );
  }
  return (
    <>
      {labelView}
      <Select {...rest} ref={ref} {...customizations} />
      {errorView}
    </>
  );
}

const ReactSelect = forwardRef(ReactSelectInner) as <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ReactSelectProps<Option, IsMulti, Group> & { ref?: React.Ref<any> }
) => React.ReactElement;

export default ReactSelect;
